import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  LayoutDashboard,
  LogOut,
  Menu,
  X,
  Network,
  Boxes,
  Shield,
  User,
  Users,
  Droplets,
  Bell,
} from "lucide-react";

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isSuperAdmin } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const getRoleBadge = () => {
    switch (user?.role) {
      case "super-admin":
        return {
          label: "Society-admin",
          icon: Shield,
          variant: "default",
          className: "bg-amber-500 text-xs",
        };
      case "aqua-admin":
        return {
          label: "super-admin (aquasense)",
          icon: Shield,
          variant: "default",
          className: "bg-cyan-600 text-xs",
        };
      case "admin":
        return {
          label: "Society-user",
          icon: User,
          variant: "secondary",
          className: "text-xs",
        };
      default:
        return {
          label: user?.role || "User",
          icon: User,
          variant: "secondary",
          className: "text-xs",
        };
    }
  };

  // Navigation items based on role
  const getNavigation = () => {
    const navigation = [
      { name: "Dashboard", href: "/dashboard", icon: LayoutDashboard },
    ];

    // Only Super Admin can see Device Registration
    if (isSuperAdmin()) {
      navigation.push(
        { name: "Setup", href: "/devices", icon: Network },
        { name: "Components", href: "/components", icon: Boxes },
      );
    }

    navigation.push(
      { name: "Analytics", href: "/analytics", icon: Droplets },
      { name: "Alerts", href: "/alerts", icon: Bell },
    );

    if (isSuperAdmin()) {
      navigation.push({ name: "Users", href: "/users", icon: Users });
    }

    return navigation;
  };

  const navigation = getNavigation();
  const roleBadge = getRoleBadge();
  const RoleBadgeIcon = roleBadge.icon;

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="w-full px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_9595d752-c849-48dc-9f4d-5cf9f8da18fb/artifacts/puwyedcw_logo.png"
                alt="AquaSense"
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-slate-900">AquaTank</h1>
                <p className="text-xs text-slate-500">Smart Water Management</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link key={item.name} to={item.href}>
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className={`gap-2 ${isActive ? "bg-primary text-white" : "text-slate-700"}`}
                      data-testid={`nav-${item.name.toLowerCase()}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </nav>

            {/* User menu */}
            <div className="flex items-center gap-4">
              <div className="hidden sm:block text-right">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-slate-900">
                    {user?.name || user?.username}
                  </p>
                  <Badge
                    variant={roleBadge.variant}
                    className={roleBadge.className}
                  >
                    <RoleBadgeIcon className="h-3 w-3 mr-1" />
                    {roleBadge.label}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">{user?.email}</p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="gap-2 text-slate-700"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden sm:inline">Logout</span>
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-200">
            <nav className="px-4 py-3 space-y-1">
              {navigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <Button
                      variant={isActive ? "default" : "ghost"}
                      className={`w-full justify-start gap-2 ${isActive ? "bg-primary text-white" : "text-slate-700"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {item.name}
                    </Button>
                  </Link>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      {/* Main content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>
    </div>
  );
};

export default Layout;
